import {
    TextAreaEntry,
    isTextAreaEntryEdited,
} from "@bpmn-io/properties-panel";
import { useService } from "bpmn-js-properties-panel";
import { is } from "bpmn-js/lib/util/ModelUtil";

const LOW_PRIORITY = 500;

/**
 * A provider with a `#getGroups(element)` method
 * that exposes groups for a diagram element.
 *
 * @param {PropertiesPanel} propertiesPanel
 * @param {Function} translate
 */
export default function ProcessPropertiesProvider(
    propertiesPanel,
    translate,
    moddle
) {
    /**
     * Return the groups provided for the given element.
     *
     * @param {DiagramElement} element
     *
     * @return {(Object[]) => (Object[])} groups middleware
     */
    this.getGroups = function (element) {
        /**
         * We return a middleware that modifies
         * the existing groups.
         *
         * @param {Object[]} groups
         *
         * @return {Object[]} modified groups
         */
        return function (groups) {
            if (is(element, "bpmn:Process")) {
                groups.push(createGroup(element, translate));
            }

            return groups;
        };
    };

    // Register our custom properties provider.
    // Use a lower priority to ensure it is loaded after
    // the basic BPMN properties.
    propertiesPanel.registerProvider(LOW_PRIORITY, this);
}

function createGroup(element, translate) {
    return {
        id: "properties-group",
        label: translate("Properties"),
        entries: [
            {
                id: "properties",
                component: Properties,
                isEdited: isTextAreaEntryEdited,
            },
        ],
    };
}

function Properties(props) {
    const { element, id } = props;

    const modeling = useService("modeling");
    const translate = useService("translate");
    const debounce = useService("debounceInput");

    const getValue = () => {
        return element.businessObject.description || "";
    };

    const setValue = (value) => {
        return modeling.updateProperties(element, {
            description: value,
        });
    };

    const entry = (
        <TextAreaEntry
            id={id}
            element={element}
            label={translate("Description")}
            description={translate("An optional process description.")}
            rows={2}
            getValue={getValue}
            setValue={setValue}
            debounce={debounce}
        />
    );

    return entry;
}

ProcessPropertiesProvider.$inject = ["propertiesPanel", "translate", "moddle"];
